// src/lib/naranjaPlus.ts
// ─────────────────────────────────────────────────────────────
// NARANJA PLUS — suscripción cloud cobrada con la billetera Flip.
//
//   * Precio: $50.000 COP / mes = 500 coins (ver src/config/pricing.ts).
//   * Al activar se descuenta del saldo Flip (`flipCharge`) y se extiende
//     `expires_at` 30 días (status = 'ACTIVE').
//   * Renovación automática si hay saldo disponible.
//   * Al REACTIVAR se dispara la resincronización de todo lo pendiente.
//   * Si está INACTIVA: la nube, la tienda digital pública y las subastas se
//     apagan, SIN borrar ni un dato local. El POS, restaurante e inventario
//     siguen funcionando al 100%.
// ─────────────────────────────────────────────────────────────

import { db, type NaranjaPlus } from '@/db/db';
import { supabase } from '@/lib/supabase';
import { purchasePlan } from '@/lib/wallet';
import { saveRowSafely } from '@/lib/cloudSchemaRepair';
import {
  NARANJA_PLUS_PRICE_COINS,
  NARANJA_PLUS_PRICE_COP,
  buildActivationUpdate,
  computeNaranjaState,
  type NaranjaPlusRow,
  type NaranjaPlusState,
} from '@/lib/naranjaPlusRules';

/** Negocio de este dispositivo. */
const currentBusinessId = async (): Promise<string> => {
  try {
    const settings = await db.settings.orderBy('created_at').last();
    return String(settings?.business_id || 'local');
  } catch {
    return 'local';
  }
};

/** Fila local de la suscripción (la crea si no existe, sin activarla). */
export const ensureSubscriptionRow = async (businessId?: string): Promise<NaranjaPlus> => {
  const bid = businessId || (await currentBusinessId());
  const existing = await db.naranja_plus.where('business_id').equals(bid).first();
  if (existing) return existing;

  const row: NaranjaPlus = {
    id: crypto.randomUUID(),
    business_id: bid,
    status: 'INACTIVE',
    expires_at: null,
    monthly_price_cop: NARANJA_PLUS_PRICE_COP,
    auto_renew: false,
    created_at: new Date().toISOString(),
    sync_status: 'LOCAL',
  };
  try {
    await db.naranja_plus.add(row);
  } catch (e) {
    console.warn('No se pudo crear la fila local de Naranja Plus:', e);
  }
  return row;
};

/** Fila cruda de la suscripción (local, refrescada desde la nube si hay). */
export const getSubscription = async (businessId?: string): Promise<NaranjaPlus> => {
  const local = await ensureSubscriptionRow(businessId);

  if (!supabase) return local;
  try {
    const { data, error } = await (supabase as any)
      .from('naranja_plus')
      .select('*')
      .eq('business_id', local.business_id)
      .maybeSingle();
    if (error || !data) return local;

    const merged: NaranjaPlus = { ...local, ...(data as any), id: local.id, sync_status: 'SYNCED' };
    await db.naranja_plus.put(merged);
    return merged;
  } catch {
    return local;
  }
};

/** ESTADO calculado (activo/gracia/inactivo + días restantes + badge). */
export const getSubscriptionState = async (businessId?: string): Promise<NaranjaPlusState> => {
  const row = await getSubscription(businessId);
  return computeNaranjaState(row as NaranjaPlusRow);
};

/** Consulta rápida (sin red) del estado local: para gates de UI. */
export const getLocalSubscriptionState = async (): Promise<NaranjaPlusState> => {
  const bid = await currentBusinessId();
  const row = await db.naranja_plus.where('business_id').equals(bid).first();
  return computeNaranjaState((row || null) as NaranjaPlusRow | null);
};

const pushToCloud = async (row: NaranjaPlus): Promise<boolean> => {
  if (!supabase) return false;

  // 🍊 El `business_id` DEBE ser el real del negocio: si la instalación quedó
  // con 'local' o vacío, no se publica una fila inválida en la nube.
  const businessId = String(row.business_id || '').trim();
  if (!businessId || businessId === 'local') {
    console.warn(`🍊 [NARANJA PLUS] No se publica en la nube: business_id inválido ("${businessId || 'vacío'}"). Configura el negocio primero.`);
    return false;
  }

  try {
    // 🛠️ Guardado seguro: upsert con auto-reparación de columnas y, si el
    // proyecto no tiene la restricción UNIQUE en business_id, respaldo
    // select → update / insert. La fila se guarda SÍ O SÍ.
    const { ok, error, method: saveMethod } = await saveRowSafely('naranja_plus', {
      business_id: businessId,
      status: row.status,
      expires_at: row.expires_at,
      monthly_price_cop: row.monthly_price_cop,
      auto_renew: row.auto_renew,
      activated_at: row.activated_at ?? null,
      last_charge_at: row.last_charge_at ?? null,
      last_charge_coins: row.last_charge_coins ?? null,
      cancel_reason: row.cancel_reason ?? null,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'business_id', keyColumn: 'business_id' });

    if (!ok) {
      console.warn('🍊 [NARANJA PLUS] No se pudo publicar en la nube:', (error as any)?.message || error);
      return false;
    }

    // Confirmación: la fila quedó guardada con el MISMO business_id.
    const { data } = await (supabase as any)
      .from('naranja_plus')
      .select('business_id, status, expires_at')
      .eq('business_id', businessId)
      .maybeSingle();
    if (data?.business_id && String(data.business_id) !== businessId) {
      console.warn(`🍊 [NARANJA PLUS] La nube devolvió otro business_id (${data.business_id}) ≠ ${businessId}. Revisa la fila en Supabase.`);
      return false;
    }
    if (!data) {
      console.warn(`🍊 [NARANJA PLUS] La fila no se encontró tras el upsert (business_id=${businessId}).`);
      return false;
    }

    await db.naranja_plus.update(row.id, { sync_status: 'SYNCED' });
    console.log(`🍊 [NARANJA PLUS] Publicado en la nube (${saveMethod}) · business_id=${businessId} · status=${data.status} · vence ${data.expires_at || '—'}`);
    return true;
  } catch (e: any) {
    console.warn('Naranja Plus: error publicando en la nube:', e?.message || e);
    return false;
  }
};

/**
 * Dispara la resincronización de todo lo pendiente (al reactivar).
 * Usa el mismo canal que el resto de la app: no inventa bucles nuevos.
 */
export const triggerFullResync = (reason = 'Reactivación de Naranja Plus'): void => {
  try {
    console.log(`🔄 [NARANJA PLUS] Resincronización solicitada: ${reason}`);
    window.dispatchEvent(new CustomEvent('niiz:sync_required'));
  } catch {
    /* entorno sin window */
  }
};

/**
 * ACTIVA/RENUEVA Naranja Plus: cobra el mes con la billetera Flip, extiende
 * `expires_at` 30 días y dispara la resincronización.
 */
export const activateNaranjaPlus = async (opts: { months?: number; businessId?: string } = {}): Promise<{
  ok: boolean;
  error?: string | undefined;
  coins?: number | undefined;
  state?: NaranjaPlusState | undefined;
  wasInactive?: boolean | undefined;
  /** ¿El estado ACTIVE quedó publicado en Supabase de inmediato? */
  cloud?: boolean | undefined;
}> => {
  const months = Math.max(1, Math.trunc(opts.months || 1));
  const local = await ensureSubscriptionRow(opts.businessId);
  const previous = computeNaranjaState(local as NaranjaPlusRow);
  const coins = NARANJA_PLUS_PRICE_COINS * months;
  const businessId = String(opts.businessId || (await currentBusinessId()));

  if (!businessId) {
    return { ok: false, error: 'Inicia sesión en la nube para contratar Naranja Plus.', coins };
  }

  // Cobro y activación en el servidor (idempotente, 30 días por mes).
  const period = new Date().toISOString().slice(0, 7);
  for (let i = 0; i < months; i++) {
    const entry = await purchasePlan('NARANJA_PLUS', `${businessId}:plan:NARANJA_PLUS:${period}:${i}`);
    if (!entry.ok) {
      return {
        ok: false,
        error:
          entry.code === 'INSUFFICIENT'
            ? `Saldo Flip insuficiente: necesitas ${coins} coins. Recarga tu billetera para activar Naranja Plus.`
            : entry.error || 'No se pudo activar Naranja Plus.',
        coins,
      };
    }
  }

  let expiresAt = local.expires_at;
  let activatedAt = local.activated_at;
  for (let i = 0; i < months; i++) {
    const patch = buildActivationUpdate({ ...local, expires_at: expiresAt, activated_at: activatedAt } as NaranjaPlusRow, coins);
    expiresAt = patch.expires_at as string;
    activatedAt = patch.activated_at as string | undefined;
  }

  const updated: NaranjaPlus = {
    ...local,
    status: 'ACTIVE',
    expires_at: expiresAt,
    monthly_price_cop: NARANJA_PLUS_PRICE_COP,
    activated_at: activatedAt || new Date().toISOString(),
    last_charge_at: new Date().toISOString(),
    last_charge_coins: coins,
    cancel_reason: null,
    updated_at: new Date().toISOString(),
    sync_status: 'LOCAL',
  };
  await db.naranja_plus.put(updated);

  // El servidor ya escribió plan_type; replicamos el plan en el caché local.
  const settingsRow = await db.settings.where('business_id').equals(businessId).first();
  if (settingsRow?.id) {
    await db.settings.update(settingsRow.id, {
      plan_type: 'NARANJA_PLUS',
      plan_expires_at: expiresAt ?? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      subscription_status: 'ACTIVE',
      auto_renew: true,
    });
  }

  // 🍊 GUARDADO INMEDIATO: el estado ACTIVE se publica en Supabase en el mismo
  // instante de la activación (la tienda pública debe abrirse de una).
  const synced = await pushToCloud(updated);
  if (!synced) {
    console.warn('🍊 [NARANJA PLUS] La activación quedó en LOCAL; se reintentará en el próximo ciclo de sincronización.');
  }

  // Reactivación → resincronizar todo lo pendiente automáticamente.
  if (!previous.isActive) triggerFullResync('Suscripción activada');
  else triggerFullResync('Suscripción renovada');

  console.log(`🍊 [NARANJA PLUS] Activado ${months} mes(es): ${coins} coins · vence ${expiresAt} · nube ${synced ? 'OK' : 'pendiente'}`);
  return { ok: true, coins, state: computeNaranjaState(updated as NaranjaPlusRow), wasInactive: !previous.isActive, cloud: synced };
};

/**
 * Reintenta publicar en la nube la suscripción local.
 *
 * 🔧 AUTO-REPARACIÓN: NO se fía del flag `sync_status`. Verifica contra la nube
 * y, si la fila NO existe (o no está ACTIVE mientras en local sí lo está),
 * fuerza el upsert inmediato con el `business_id` real y su `expires_at`.
 * Nunca lanza: un fallo de Naranja Plus jamás interrumpe el lote de ventas.
 */
export const syncSubscriptionToCloud = async (
  businessId?: string,
  opts: { force?: boolean } = {},
): Promise<boolean> => {
  if (!supabase) return false;
  try {
    const bid = businessId || (await currentBusinessId());
    const row = await db.naranja_plus.where('business_id').equals(bid).first();
    if (!row) return false;

    const localActive = computeNaranjaState(row as NaranjaPlusRow).isActive
      || String(row.status || '').toUpperCase() === 'ACTIVE';

    // Se comprueba el estado REAL en la nube (no el flag local).
    let cloudRow: any = null;
    try {
      const { data, error } = await (supabase as any)
        .from('naranja_plus')
        .select('business_id, status, expires_at')
        .eq('business_id', bid)
        .maybeSingle();
      if (!error) cloudRow = data;
    } catch { /* se intentará publicar igual */ }

    const cloudActive = String(cloudRow?.status || '').toUpperCase() === 'ACTIVE';

    if (!opts.force && cloudRow && cloudActive) {
      // Todo correcto: solo se sincroniza el flag local.
      if (row.sync_status !== 'SYNCED') {
        try { await db.naranja_plus.update(row.id, { sync_status: 'SYNCED' }); } catch { /* noop */ }
      }
      return true;
    }

    if (!cloudRow) {
      console.warn(`🍊 [NARANJA PLUS] La nube NO tiene fila para business_id=${bid}. Se crea ahora${localActive ? ' (local ACTIVE)' : ''}.`);
    } else if (!cloudActive && localActive) {
      console.warn(`🍊 [NARANJA PLUS] La nube tiene status=${cloudRow.status} pero en local está ACTIVE. Se corrige.`);
    }

    // ⚡ Con la suscripción activa en local se FUERZA la fila con el payload
    // exacto de activación (status ACTIVE + expires_at) y se confirma.
    if (localActive) return await forceActivateCloudRow(bid);
    return await pushToCloud(row);
  } catch (e) {
    console.warn('Naranja Plus: no se pudo reintentar la publicación:', e);
    return false;
  }
};

/** ¿La nube tiene la fila de Naranja Plus para este negocio? (para la UI) */
export const checkCloudSubscriptionRow = async (
  businessId?: string,
): Promise<{ exists: boolean; status: string | null; expiresAt: string | null; error: string | null }> => {
  if (!supabase) return { exists: false, status: null, expiresAt: null, error: 'Supabase no configurado' };
  try {
    const bid = businessId || (await currentBusinessId());
    const { data, error } = await (supabase as any)
      .from('naranja_plus')
      .select('business_id, status, expires_at')
      .eq('business_id', bid)
      .maybeSingle();
    if (error) return { exists: false, status: null, expiresAt: null, error: error.message };
    return {
      exists: !!data,
      status: data?.status ?? null,
      expiresAt: data?.expires_at ?? null,
      error: null,
    };
  } catch (e: any) {
    return { exists: false, status: null, expiresAt: null, error: e?.message || 'error' };
  }
};

/**
 * ⚡ CREACIÓN FORZADA DE LA FILA EN `public.naranja_plus`.
 *
 * Se usa cuando en local la suscripción está ACTIVA pero la nube no tiene fila
 * (el caso "status=SIN FILA" que cerraba la tienda digital). Envía el payload
 * EXACTO de activación y luego CONFIRMA leyendo la fila.
 *
 * Si falta `expires_at`, se otorgan 30 días calendario desde ahora.
 */
export const forceActivateCloudRow = async (businessId?: string): Promise<boolean> => {
  if (!supabase) return false;
  try {
    const bid = String(businessId || (await currentBusinessId())).trim();
    if (!bid || bid === 'local') {
      console.warn(`🍊 [NARANJA PLUS] No se puede forzar la fila: business_id inválido ("${bid || 'vacío'}").`);
      return false;
    }

    const local = await db.naranja_plus.where('business_id').equals(bid).first();
    const localActive = !!local && (
      computeNaranjaState(local as NaranjaPlusRow).isActive ||
      String(local.status || '').toUpperCase() === 'ACTIVE'
    );
    if (!localActive) {
      console.warn(`🍊 [NARANJA PLUS] No se fuerza la fila: la suscripción local no está ACTIVA (business_id=${bid}).`);
      return false;
    }

    // Fecha de vencimiento: la local si es futura; si no, +30 días.
    const localExpires = local?.expires_at ? new Date(String(local.expires_at)) : null;
    const expiresAt = localExpires && localExpires.getTime() > Date.now()
      ? localExpires.toISOString()
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

    // 📦 PAYLOAD EXACTO de activación.
    const payload = {
      business_id: bid,
      status: 'ACTIVE',
      expires_at: expiresAt,
      monthly_price_cop: Number(local?.monthly_price_cop) || NARANJA_PLUS_PRICE_COP,
      auto_renew: local?.auto_renew ?? true,
      updated_at: new Date().toISOString(),
    };

    const { ok, error, method } = await saveRowSafely('naranja_plus', payload, {
      onConflict: 'business_id',
      keyColumn: 'business_id',
    });
    if (!ok) {
      console.warn('🍊 [NARANJA PLUS] No se pudo crear la fila en la nube:', (error as any)?.message || error);
      return false;
    }
    console.log(`🍊 [NARANJA PLUS] Fila guardada con el método ${method} · business_id=${bid}`);

    // ✅ CONFIRMACIÓN: se vuelve a leer la fila recién creada.
    const check = await checkCloudSubscriptionRow(bid);
    if (local?.id) {
      try { await db.naranja_plus.update(local.id, { sync_status: check.exists ? 'SYNCED' : 'LOCAL' }); } catch { /* noop */ }
    }

    if (!check.exists) {
      console.warn(`🍊 [NARANJA PLUS] El upsert no dejó fila visible para business_id=${bid}. Revisa RLS/GRANT de public.naranja_plus.`);
      return false;
    }

    console.log(
      `🍊 [NARANJA PLUS] Fila FORZADA y confirmada en la nube · business_id=${bid} · status=${check.status} · vence ${check.expiresAt || expiresAt}`,
    );
    return true;
  } catch (e: any) {
    console.warn('🍊 [NARANJA PLUS] Error forzando la fila en la nube:', e?.message || e);
    return false;
  }
};

/** Activa o desactiva la renovación automática. */
export const setAutoRenew = async (enabled: boolean): Promise<NaranjaPlus> => {
  const local = await ensureSubscriptionRow();
  const updated = { ...local, auto_renew: enabled, updated_at: new Date().toISOString(), sync_status: 'LOCAL' as const };
  await db.naranja_plus.put(updated);
  await pushToCloud(updated);
  return updated;
};

/**
 * Refleja en `naranja_plus` (local + nube) una activación de plan ya cobrada
 * por el servidor. No cobra: solo publica el estado para la tienda y la UI.
 */
export const registerPlanActivation = async (
  plan: 'NARANJA_PLUS' | 'ADVANCED_ACCOUNTING' | 'STUDIO_FIRM',
  coins = 0,
): Promise<void> => {
  const businessId = await currentBusinessId();
  if (!businessId || businessId === 'local') return;
  const existing = await ensureSubscriptionRow(businessId);
  const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
  const updated: NaranjaPlus = {
    ...existing,
    business_id: businessId,
    status: 'ACTIVE',
    expires_at: expiresAt,
    monthly_price_cop: plan === 'NARANJA_PLUS' ? NARANJA_PLUS_PRICE_COP : existing.monthly_price_cop,
    activated_at: existing.activated_at || new Date().toISOString(),
    last_charge_at: new Date().toISOString(),
    last_charge_coins: coins,
    auto_renew: true,
    cancel_reason: null,
    updated_at: new Date().toISOString(),
    sync_status: 'LOCAL',
  };
  await db.naranja_plus.put(updated);
  await pushToCloud(updated);
};

/**
 * Estado de renovación. El cobro mensual lo ejecuta el cron del servidor
 * (`run_subscription_billing`); el cliente NO vuelve a cobrar para evitar
 * doble débito. Esta función solo refleja el estado local.
 */
export const runAutoRenewIfNeeded = async (): Promise<{ renewed: boolean; state: NaranjaPlusState }> => {
  const local = await ensureSubscriptionRow();
  const state = computeNaranjaState(local as NaranjaPlusRow);
  if (!state.isActive && local.status !== 'INACTIVE') {
    const updated: NaranjaPlus = { ...local, status: 'INACTIVE', cancel_reason: 'Plan vencido', updated_at: new Date().toISOString(), sync_status: 'LOCAL' };
    await db.naranja_plus.put(updated);
    await pushToCloud(updated);
    return { renewed: false, state: computeNaranjaState(updated as NaranjaPlusRow) };
  }
  return { renewed: false, state };
};

/** Cancela la suscripción (deja de renovar; el acceso sigue hasta vencer). */
export const cancelNaranjaPlus = async (): Promise<NaranjaPlus> => {
  const local = await ensureSubscriptionRow();
  const updated: NaranjaPlus = {
    ...local,
    auto_renew: false,
    cancel_reason: 'Cancelada por el negocio',
    updated_at: new Date().toISOString(),
    sync_status: 'LOCAL',
  };
  await db.naranja_plus.put(updated);
  await pushToCloud(updated);
  return updated;
};

/** Realtime: cambios de suscripción (otro dispositivo del mismo negocio). */
export const subscribeToNaranjaPlus = (onChange: (row: NaranjaPlusRow) => void): (() => void) => {
  if (!supabase) return () => {};
  try {
    const channel = (supabase as any)
      .channel('niiz_naranja_plus')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'naranja_plus' }, (payload: any) => {
        const row = payload?.new || payload?.record;
        if (row?.business_id) onChange(row as NaranjaPlusRow);
      })
      .subscribe();
    return () => { try { (supabase as any).removeChannel(channel); } catch { /* noop */ } };
  } catch {
    return () => {};
  }
};

/**
 * Estado de la suscripción de OTRO negocio (tienda pública).
 * Lee de la nube por business_id; si no hay, asume inactivo (tienda cerrada).
 */
export const getBusinessSubscriptionState = async (businessId: string): Promise<NaranjaPlusState> =>
  (await getBusinessSubscription(businessId)).state;

export interface BusinessSubscriptionInfo {
  state: NaranjaPlusState;
  /** ¿Se encontró fila de suscripción (en la nube o en el caché local)? */
  found: boolean;
  row: NaranjaPlusRow | null;
  /** Error de lectura (red/RLS). Si hay error y no hay fila, NO se debe cerrar la tienda. */
  error: string | null;
  source: 'CLOUD' | 'LOCAL' | 'NONE';
}

/**
 * ESTADO DE LA SUSCRIPCIÓN DE UN NEGOCIO (tienda digital pública).
 *
 * 🔓 LECTURA PÚBLICA: `public.naranja_plus` tiene política RLS de SELECT para
 * `anon` y `authenticated` (ver supabase/setup_naranja_plus.sql), así que un
 * visitante SIN sesión puede saber si la tienda está abierta.
 *
 * Regla de apertura: si `status === 'ACTIVE'` y no ha vencido → ABIERTA.
 * Si la lectura falla por un problema técnico (red/RLS) se devuelve `error`
 * para que la tienda NO se cierre por un fallo nuestro (offline-first).
 */
export const getBusinessSubscription = async (businessId: string): Promise<BusinessSubscriptionInfo> => {
  const bid = String(businessId || '').trim();
  if (!bid) {
    return { state: computeNaranjaState(null), found: false, row: null, error: 'business_id vacío', source: 'NONE' };
  }

  const normalize = (row: NaranjaPlusRow | null): NaranjaPlusState => {
    const state = computeNaranjaState(row);
    // 🍊 ACTIVE sin fecha de vencimiento = suscripción vigente (se muestra ABIERTA).
    const declaredActive = String((row as any)?.status || '').toUpperCase() === 'ACTIVE';
    if (declaredActive && !state.isActive) {
      return { ...state, status: 'ACTIVE', isActive: true, badge: '🍊 Naranja Plus: Activo' };
    }
    return state;
  };

  let cloudError: string | null = null;

  // 1) NUBE (lectura pública).
  if (supabase) {
    try {
      const { data, error } = await (supabase as any)
        .from('naranja_plus')
        .select('*')
        .eq('business_id', bid)
        .maybeSingle();
      if (error) {
        cloudError = error.message || 'error leyendo naranja_plus';
        console.warn('🍊 [TIENDA] No se pudo leer Naranja Plus de la nube:', cloudError);
      } else if (data) {
        // Se cachea en local para que la tienda funcione offline.
        try {
          const cached = { ...(data as any), id: (data as any).id || `np-${bid}`, sync_status: 'SYNCED' };
          await db.naranja_plus.put(cached);
        } catch { /* caché opcional */ }
        return { state: normalize(data as NaranjaPlusRow), found: true, row: data as NaranjaPlusRow, error: null, source: 'CLOUD' };
      }
    } catch (e: any) {
      cloudError = e?.message || 'excepción leyendo naranja_plus';
      console.warn('🍊 [TIENDA] Excepción leyendo Naranja Plus:', cloudError);
    }
  }

  // 2) CACHÉ LOCAL (este navegador puede ser el del negocio o haber leído antes).
  try {
    const local = (await db.naranja_plus.where('business_id').equals(bid).first()) as NaranjaPlusRow | undefined;
    if (local) {
      return { state: normalize(local), found: true, row: local, error: cloudError, source: 'LOCAL' };
    }
  } catch { /* sin caché */ }

  return { state: computeNaranjaState(null), found: false, row: null, error: cloudError, source: 'NONE' };
};
